"use client";

import { useEffect, useRef } from "react";

import { cn } from "@/lib/utils";
import { Icon } from "./icon";

/** Offset for sticky dashboard header when scrolling an error into view. */
const ERROR_SCROLL_MARGIN_CLASS = "scroll-mt-28";

/** Scroll an error element into view (smooth). Use when not rendering `ErrorAlert`. */
export function scrollErrorIntoView(el: HTMLElement | null) {
  if (!el) return;
  requestAnimationFrame(() => {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  });
}

export function EmptyState({
  title,
  description,
  action,
  icon = "inbox",
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  icon?: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-outline-variant bg-surface-container-low px-6 py-16 text-center">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-surface-container text-outline">
        <Icon name={icon} size="lg" />
      </div>
      <h3 className="text-headline-md font-semibold text-on-surface">{title}</h3>
      {description ? (
        <p className="mt-2 max-w-sm text-body-md text-on-surface-variant">{description}</p>
      ) : null}
      {action ? <div className="mt-6">{action}</div> : null}
    </div>
  );
}

/** Pure-CSS spinner — a bordered ring, so it never flashes an unstyled icon-font
 * glyph (e.g. the literal text "progress_activity") before the font loads. */
export function Spinner({ className }: { className?: string }) {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={cn(
        "inline-block h-5 w-5 animate-spin rounded-full border-2 border-outline-variant border-t-primary",
        className,
      )}
    />
  );
}

export function LoadingBlock({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="flex items-center gap-3 text-body-md text-on-surface-variant">
        <Spinner />
        {label}
      </div>
    </div>
  );
}

/** Shimmering placeholder block for content-shaped loading states. */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div className={cn("animate-pulse rounded-md bg-surface-container", className)} />
  );
}

export function ErrorAlert({ message }: { message: string }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollErrorIntoView(ref.current);
  }, [message]);

  return (
    <div
      ref={ref}
      role="alert"
      className={cn(
        "flex items-start gap-2 rounded-lg border border-error/30 bg-error-container px-4 py-3 text-body-md text-on-error-container",
        ERROR_SCROLL_MARGIN_CLASS,
      )}
    >
      <Icon name="error" size="sm" className="mt-0.5 shrink-0" />
      <span>{message}</span>
    </div>
  );
}
